import { api } from '@agency/shared';
import { Button, Chip, DataTable, PageHeader, type DataTableColumn } from '@agency/ui';
import { UserPlus } from 'lucide-react';
import { useMemo, useState } from 'react';
import { ErrorCallout, FilterSelect, LoadMore, Page, RoleChips, Stamp, TableCard, UserLink } from '../../app/widgets.js';
import { INVITATION_TONE } from '../../lib/labels.js';
import { useMutation } from '../../lib/mutate.js';
import { useSession } from '../../lib/session.js';
import { useApi, usePaged } from '../../lib/useApi.js';
import { InviteDialog } from './InviteDialog.js';

export function InvitationsPage() {
  const session = useSession();
  const mutation = useMutation();
  const [status, setStatus] = useState<api.InvitationStatus | undefined>('pending');
  const [inviteOpen, setInviteOpen] = useState(false);
  const input = useMemo(() => ({ limit: 100, ...(status ? { status } : {}) }), [status]);
  const invitations = usePaged('admin.invitations.list', { input });
  const accounts = useApi('admin.accounts.list', { input: { limit: 200 } }, { enabled: session.has(['account.assign', 'account.register', 'audit.read']) });

  const columns = useMemo<DataTableColumn<api.Invitation>[]>(
    () => [
      { id: 'who', header: 'Invitee', cell: ({ row }) => <span><span className="text-ink">{row.original.displayName ?? row.original.email}</span> <span className="font-mono text-2xs text-mute">{row.original.email}</span></span>, size: 300 },
      { id: 'roles', header: 'Roles', cell: ({ row }) => <RoleChips roles={row.original.roles} />, size: 200 },
      { id: 'status', header: 'Status', cell: ({ row }) => <Chip tone={INVITATION_TONE[row.original.status]}>{row.original.status}</Chip>, size: 100 },
      { id: 'delivery', header: 'Delivery', cell: ({ row }) => <span className="font-mono text-xs-plus text-mute">{row.original.deliveryState ?? '--'}</span>, size: 90 },
      { id: 'by', header: 'Invited by', cell: ({ row }) => <UserLink userId={row.original.invitedByUserId} />, size: 150 },
      { id: 'created', header: 'Created', cell: ({ row }) => <Stamp value={row.original.createdAt} />, size: 150 },
      { id: 'expires', header: 'Expires', cell: ({ row }) => <Stamp value={row.original.expiresAt} relative />, size: 100 },
      { id: 'resends', header: 'Resends', cell: ({ row }) => <span className="block text-right font-mono tabular-nums">{row.original.resendCount}</span>, size: 80 },
      {
        id: 'actions',
        header: '',
        size: 170,
        cell: ({ row }) =>
          row.original.status !== 'pending' ? null : (
            <span className="flex gap-1">
              <Button size="sm" variant="tertiary" onClick={() => void mutation.run('admin.invitations.resend', { params: { invitationId: row.original.id }, input: {} }, { title: 'Invitation resent', invalidate: ['admin.invitations', 'admin.people'] })}>Resend</Button>
              <Button size="sm" variant="tertiary" onClick={() => void mutation.run('admin.invitations.cancel', { params: { invitationId: row.original.id }, input: {} }, { title: 'Invitation cancelled', invalidate: ['admin.invitations', 'admin.people'] })}>Cancel</Button>
            </span>
          ),
      },
    ],
    [mutation],
  );

  return (
    <Page>
      <PageHeader
        eyebrow="People and access"
        eyebrowPath="Invitations"
        title="Invitations"
        subtitle="Single-use tokens bound to the invited identity, 7-day lifetime. Resend rotates the token; there is never more than one active token per person."
        actions={<Button variant="primary" icon={UserPlus} disabled={!session.has('people.manage')} onClick={() => setInviteOpen(true)}>Invite person</Button>}
      />
      <ErrorCallout error={invitations.error} onRetry={() => void invitations.reload()} className="mt-6" />
      <div className="mt-6">
        <TableCard title="Invitations" subtitle={`${invitations.items.length} shown`} footer={<LoadMore hasMore={invitations.hasMore} loading={invitations.loadingMore} onLoadMore={() => void invitations.loadMore()} />}>
          <div className="border-b border-hair px-5 py-3">
            <FilterSelect label="Status" value={status} onChange={(v) => setStatus(v as api.InvitationStatus | undefined)} options={api.INVITATION_STATUSES.map((s) => ({ value: s, label: s }))} width={160} />
          </div>
          <DataTable<api.Invitation> aria-label="Invitations" columns={columns} rows={invitations.items} rowKey={(r) => r.id} height={Math.min(640, Math.max(200, invitations.items.length * 34 + 40))} state={invitations.loading ? 'loading' : invitations.items.length === 0 ? 'empty' : 'complete'} emptyMessage="No invitations in this state." noun="invitations" />
        </TableCard>
      </div>
      <InviteDialog open={inviteOpen} onOpenChange={setInviteOpen} accounts={accounts.data?.items ?? []} />
    </Page>
  );
}
